import type { GoalsStore, GoalProgress } from './index';
import type { ClientGoal } from '@/types/goals';

const KEY = 'coachd-client-goals';

const readAll = (): Record<string, ClientGoal[]> => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}') || {};
  } catch {
    return {};
  }
};

const writeAll = (map: Record<string, ClientGoal[]>) => {
  localStorage.setItem(KEY, JSON.stringify(map));
};

export const localGoalsStore: GoalsStore = {
  async getClientGoals(clientId) {
    const all = readAll();
    return Array.isArray(all[clientId]) ? all[clientId] : [];
  },
  async setClientGoals(clientId, goals) {
    const all = readAll();
    all[String(clientId)] = goals;
    writeAll(all);
  },

  // Additional methods for compatibility
  async listGoals(clientId: string): Promise<ClientGoal[]> {
    return this.getClientGoals(clientId);
  },

  async createGoal(goal: Omit<ClientGoal, 'id' | 'createdAt' | 'updatedAt'>): Promise<ClientGoal> {
    const now = new Date().toISOString();
    const newGoal: ClientGoal = {
      ...goal,
      id: `goal_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: now,
      updatedAt: now,
      progress: 0,
      status: 'active',
      history: [],
    };

    const goals = await this.getClientGoals(goal.clientId);
    goals.push(newGoal);
    await this.setClientGoals(goal.clientId, goals);
    return newGoal;
  },

  async updateGoal(goalId: string, updates: Partial<ClientGoal>): Promise<ClientGoal> {
    // Find the goal across all clients
    const all = readAll();
    for (const [_clientId, goals] of Object.entries(all)) {
      const goalIndex = goals.findIndex((g) => g.id === goalId);
      if (goalIndex !== -1) {
        const updatedGoal = { ...goals[goalIndex], ...updates, updatedAt: new Date().toISOString() };
        goals[goalIndex] = updatedGoal;
        writeAll(all);
        return updatedGoal;
      }
    }
    throw new Error('Goal not found');
  },

  async deleteGoal(goalId: string): Promise<void> {
    const all = readAll();
    for (const [clientId, goals] of Object.entries(all)) {
      const filteredGoals = goals.filter((g) => g.id !== goalId);
      if (filteredGoals.length !== goals.length) {
        all[clientId] = filteredGoals;
        writeAll(all);
        return;
      }
    }
  },

  async addGoalProgress(goalId: string, progress: { value: number; note?: string }): Promise<GoalProgress> {
    const now = new Date().toISOString();
    const progressEntry: GoalProgress = {
      id: `gp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      goalId,
      value: progress.value,
      note: progress.note,
      createdAt: now,
      createdBy: 'client',
    };

    // Update the goal's current value and history
    const all = readAll();
    for (const [_clientId, goals] of Object.entries(all)) {
      const goalIndex = goals.findIndex((g) => g.id === goalId);
      if (goalIndex !== -1) {
        const goal = goals[goalIndex];
        goal.currentValue = progress.value;
        goal.history = goal.history || [];
        goal.history.push({
          id: progressEntry.id,
          at: now,
          value: progress.value,
          note: progress.note,
        });
        goal.updatedAt = now;
        writeAll(all);
        return progressEntry;
      }
    }
    throw new Error('Goal not found');
  },

  async listGoalProgress(goalId: string): Promise<GoalProgress[]> {
    const all = readAll();
    for (const goals of Object.values(all)) {
      const goal = goals.find((g) => g.id === goalId);
      if (goal && goal.history) {
        return goal.history.map((h) => ({
          id: h.id,
          goalId,
          value: h.value,
          note: h.note,
          createdAt: h.at,
          createdBy: 'client' as const,
        }));
      }
    }
    return [];
  },

  async getCoachGoalsForClient(clientId: string, coachId: string): Promise<ClientGoal[]> {
    const goals = await this.getClientGoals(clientId);
    return goals.filter((g) => g.ownerCoachId === coachId);
  },
};
